import { Link } from 'react-router-dom'
import useAuthHook from '../userAuth'

const cards = {
  admin: [
    {
      to: '/tokens',
      title: 'Tokens',
      color: 'border-l-blue-500 bg-blue-50',
      items: [
        'Create and edit API access tokens',
        'Manage tokens for the documentation portal',
        'Assign tokens to customer accounts'
      ]
    },
    {
      to: '/customers',
      title: 'Customers',
      color: 'border-l-green-500 bg-green-50',
      items: [
        'Add or remove authorized email addresses',
        'Create and assign API keys',
        'Configure usage plan associations',
        'Assign documentation access tokens'
      ]
    },
    {
      to: '/api-usage',
      title: 'API Usage',
      color: 'border-l-purple-500 bg-purple-50',
      items: [
        'Run analytics reports across all keys',
        'Filter by key, environment, and date range',
        'Export summaries, transactions, and billables'
      ]
    },
    {
      to: '/api',
      title: 'Medipro Browser',
      color: 'border-l-amber-500 bg-amber-50',
      items: [
        'Test Medipro API endpoints interactively',
        'Create contacts, set health and medications',
        'Run Predict Plans with live responses'
      ]
    },
    {
      to: '/activity-log',
      title: 'Activity Log',
      color: 'border-l-red-500 bg-red-50',
      items: [
        'Review all admin actions chronologically',
        'Filter by user, action, or details',
        'Export the full log as CSV'
      ]
    },
    {
      to: '/settings',
      title: 'Settings',
      color: 'border-l-teal-500 bg-teal-50',
      items: [
        'Edit customer welcome invitation emails',
        'Edit token access notification emails',
        'Copy formatted emails to clipboard'
      ]
    }
  ],
  customer: [
    {
      to: '/my-keys',
      title: 'My Keys',
      color: 'border-l-blue-500 bg-blue-50',
      items: [
        'View your assigned API keys and tokens',
        'Reveal and copy key values',
        'Access the documentation portal'
      ]
    },
    {
      to: '/api-usage',
      title: 'API Usage',
      color: 'border-l-purple-500 bg-purple-50',
      items: [
        'Run analytics reports on your API usage',
        'View call volumes by endpoint',
        'Export summaries and transaction details'
      ]
    },
    {
      to: '/api',
      title: 'Medipro Browser',
      color: 'border-l-amber-500 bg-amber-50',
      items: [
        'Test Medipro API endpoints interactively',
        'Create contacts, set health and medications',
        'Run Predict Plans with live responses'
      ]
    }
  ]
}

const HomePage = ({ role, customer }) => {
  const { user } = useAuthHook()

  let userCards = cards[role] || []

  // For customers, filter cards based on API access flags
  if (role === 'customer' && customer) {
    if (customer.isMediproCustomer === false) {
      userCards = userCards.filter((card) => card.to !== '/api')
    }
  }

  return (
    <div className='text-center py-2 text-blue-10'>
      {user && (
        <div className='max-w-6xl mx-auto px-4 py-6'>
          <p className='text-2xl sm:text-3xl font-semibold mb-1'>Welcome</p>
          <p className='text-lg sm:text-xl mb-2'>{user.email}</p>
          <p className='text-sm text-gray-500 mb-6'>Get started by selecting an option below or from the navigation bar above.</p>
          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4'>
            {userCards.map((card) => (
              <Link key={card.to} to={card.to} className='block'>
                <div className={`border-l-4 rounded-lg p-5 shadow-sm hover:shadow-md transition text-left h-full ${card.color}`}>
                  <h3 className='text-lg font-bold mb-2'>{card.title}</h3>
                  <ul className='text-sm text-gray-700 space-y-1 list-disc ml-4'>
                    {card.items.map((item, i) => (
                      <li key={i}>{item}</li>
                    ))}
                  </ul>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
      {!user && (
        <div className='max-w-2xl mx-auto py-8 px-4'>
          <div className='bg-white rounded-lg shadow-lg p-8 text-left'>
            <h1 className='text-3xl font-bold mb-4 text-center'>IHT Self-Service</h1>
            <p className='text-base text-gray-700 mb-4'>
              The IHT Self-Service platform by Inspire Health Tech provides authorized customers and administrators with tools to manage API keys, access tokens, and view usage analytics for the Medipro API.
            </p>
            <ul className='list-disc ml-6 text-base text-gray-700 mb-6 space-y-1'>
              <li>Manage API keys and access tokens</li>
              <li>View API usage analytics and reports</li>
              <li>Test API endpoints interactively</li>
            </ul>
            <p className='text-base text-gray-700'>
              Access is limited to authorized users. Please sign in to continue.
            </p>
            <div className='mt-6 text-center'>
              <Link to='/login' className='inline-block rounded bg-blue-6 hover:bg-blue-7 px-6 py-3 text-white text-base transition'>
                Sign In
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default HomePage
